import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Mutation,
  MutationAssignToAccessGrantsArgs,
  MutationUnassignFromAccessGrantsArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ASSIGN_TO_ACCESS_GRANTS, UNASSIGN_TO_ACCESS_GRANTS } from 'graphql/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// The locations (device groups) only hold the ids of their doors (devices); the doors query has the door details.
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;
const ASSIGN_TO_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'assignToAccessGrants'>,
  MutationAssignToAccessGrantsArgs
> = ASSIGN_TO_ACCESS_GRANTS;
const UNASSIGN_TO_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unassignFromAccessGrants'>,
  MutationUnassignFromAccessGrantsArgs
> = UNASSIGN_TO_ACCESS_GRANTS;

interface Props extends BaseHookProps {
  visitorGroupId: string;
}
const useDevicesTab = ({ handleFetchError, visitorGroupId }: Props) => {
  const { translation } = useTranslation();
  const {
    loading: locationsLoading,
    error: locationsError,
    data: locationsData
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED);

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.device_group_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const { loading: doorsLoading, error: doorsError, data: doorsData } = useQuery(GET_DOORS_FOR_TENANT_TYPED);

  // Cached data is shown straight away; only the very first load shows the loader.
  const deviceGroupsLoading = locationsLoading && !locationsData;
  const devicesLoading = doorsLoading && !doorsData;

  // True when no query is on its first load and at least one with cached data is refreshing. `loading` is used instead
  // of the network status because a failed refetch leaves the status on `error`, which would keep the "refreshing"
  // indicator on until the next success.
  useRegisterBackgroundRefetch(
    !deviceGroupsLoading && !devicesLoading && ((!!locationsData && locationsLoading) || (!!doorsData && doorsLoading))
  );

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const [assignToAccessGrantsMutation, { loading: assignToAccessGrantsMutationLoading }] = useMutation(
    ASSIGN_TO_ACCESS_GRANTS_TYPED,
    {
      onError: (error) => {
        handleFetchError(translation.access_grant_error_adding);
        console.error(error);
      },
      refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
      awaitRefetchQueries: true
    }
  );

  const [unassignFromAccessGrantsMutation, { loading: unassignFromAccessGrantsMutationLoading }] = useMutation(
    UNASSIGN_TO_ACCESS_GRANTS_TYPED,
    {
      onError: (error) => {
        handleFetchError(translation.access_grant_error_removing);
        console.error(error);
      },
      refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
      awaitRefetchQueries: true
    }
  );

  const assignToAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    const { data } = await assignToAccessGrantsMutation({
      variables: {
        accessGrantIds,
        doorIds,
        locationIds
      }
    });
    return data;
  };

  const unassignFromAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    const { data } = await unassignFromAccessGrantsMutation({
      variables: {
        accessGrantIds,
        doorIds,
        locationIds
      }
    });
    return data;
  };

  return {
    deviceGroups: locationsData?.getLocationsForTenant || [],
    deviceGroupsLoading,
    doors: doorsData?.getDoorsForTenant ?? [],
    doorsLoading: devicesLoading,
    assignToAccessGrants,
    assignToAccessGrantsMutationLoading,
    unassignFromAccessGrants,
    unassignFromAccessGrantsMutationLoading
  };
};

export default useDevicesTab;
